import { useCallback } from "react";
import { toast } from "sonner";

import buildRoundPayload from "@/utils/buildRoundPayload";

const apiUrl = process.env.REACT_APP_BACKEND_URL;

export default function useBallotActions({
  ballot,
  isPro,
  navigate,
  onRoundReset,
  onRoundUnlock,
  resetBallot,
  resetTimer,
  resolvedBestSpeakerId,
  setActiveStage,
  setSavedToAccount,
  teams,
}) {
  const createPayload = useCallback(
    () => buildRoundPayload({ ballot, resolvedBestSpeakerId, teams }),
    [ballot, resolvedBestSpeakerId, teams],
  );

  const saveBallot = useCallback(async () => {
    if (!isPro) {
      toast.info("Saved round history is available with PFJudgePro Pro.");
      navigate("/pro");
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/rounds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(createPayload()),
      });
      if (!response.ok) throw new Error("History save failed");
      setSavedToAccount(true);
      toast.success("Round saved to your private Pro history.");
    } catch (error) {
      toast.error("This round could not be saved. Please try again.");
    }
  }, [createPayload, isPro, navigate, setSavedToAccount]);

  const newRound = useCallback(() => {
    const confirmed = window.confirm(
      "Start a new ballot? Your current draft will be replaced in this browser tab.",
    );
    if (!confirmed) return;
    resetBallot();
    setSavedToAccount(false);
    setActiveStage("Constructive");
    resetTimer();
    onRoundUnlock();
    onRoundReset();
  }, [
    onRoundReset,
    onRoundUnlock,
    resetBallot,
    resetTimer,
    setActiveStage,
    setSavedToAccount,
  ]);

  const exportBallot = useCallback(async () => {
    if (!isPro) {
      toast.info("Exports are available with PFJudgePro Pro.");
      navigate("/pro");
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/rounds/export-text`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(createPayload()),
      });
      if (!response.ok) throw new Error("Text export failed");
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = "pfjudgepro-ballot.txt";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Ballot exported as a text file");
    } catch (error) {
      toast.error("Text export could not be created. Please try again.");
    }
  }, [createPayload, isPro, navigate]);

  const exportPdf = useCallback(async () => {
    const response = await fetch(`${apiUrl}/api/rounds/export-pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(createPayload()),
    });
    if (!response.ok) {
      toast.error("Complete PDF export is available with Pro.");
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = "pfjudgepro-complete-ballot.pdf";
    link.click();
    URL.revokeObjectURL(url);
  }, [createPayload]);

  return { exportBallot, exportPdf, newRound, saveBallot };
}